/**
 * DiagramActionsMenu Component
 *
 * The per-diagram actions menu shown on each grid card and table row of the
 * Diagram_Browser (passed as the `renderActions` slot by `DiagramsInterface`).
 * It offers Open, Copy embed, Open in GitLab, History, and Delete. See
 * Requirements 9.7, 9.8, 11.1, and 11.2.
 *
 * - Open navigates to the editor route (`/diagrams/{id}`).
 * - Copy embed copies the Embed_Snippet to the clipboard (the shared
 *   `CopyEmbedButton`, rendered as a menu row).
 * - Open in GitLab opens the file's page on the default branch in a new tab.
 * - History opens the editor with the history panel open (`?history=1`), which
 *   the editor reads to show the history panel on load (task 6.5).
 * - Delete asks for confirmation (warning that embeds will show a broken image,
 *   per Requirement 9.8) and calls `deleteDiagram`; a 423 `LOCKED_BY_OTHER`
 *   response shows a toast naming who is editing, so the user knows why it was
 *   refused.
 *
 * Delete is a controlled `AlertDialog` driven from the menu item rather than a
 * nested trigger: selecting a Radix menu item dismisses the menu, which would
 * also unmount a trigger nested inside it, so the dialog's open state is kept
 * here instead.
 */

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { ExternalLink, History, Loader2, MoreHorizontal, SquarePen, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import CopyEmbedButton from "@/components/diagrams/CopyEmbedButton"
import {
  buildGitLabFileUrl,
  deleteDiagram,
  DiagramsApiError,
  type DiagramSummary,
  type RepoInfo,
} from "@/services/diagramsService"

/** Props for the DiagramActionsMenu component. */
interface DiagramActionsMenuProps {
  /** The diagram the menu acts on. */
  diagram: DiagramSummary
  /** The repo info, used to build the GitLab file URL and the Embed_Snippet. */
  repo: RepoInfo
  /** Returns a fresh Auth0 access token for the delete request. */
  getAccessToken: () => Promise<string>
  /** Called to open the diagram in the editor (navigation is delegated here). */
  onOpen: (diagram: DiagramSummary) => void
  /** Called after a successful delete so the browser can refresh its list. */
  onDeleted: () => void
}

/**
 * The actions menu for a single diagram.
 *
 * @param props - Component props.
 * @param props.diagram - The diagram the menu acts on.
 * @param props.repo - The repo info for GitLab URLs and the Embed_Snippet.
 * @param props.getAccessToken - Token getter for the delete request.
 * @param props.onOpen - Handler to open the diagram in the editor.
 * @param props.onDeleted - Handler invoked after a successful delete.
 * @returns The rendered actions menu.
 */
export default function DiagramActionsMenu({
  diagram,
  repo,
  getAccessToken,
  onOpen,
  onDeleted,
}: DiagramActionsMenuProps): React.JSX.Element {
  const navigate = useNavigate()

  // Controls the delete confirmation dialog, kept here rather than nested in a
  // menu item (see the file docstring for why).
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false)
  // Whether a delete request is in flight, to disable the confirm button.
  const [deleting, setDeleting] = useState<boolean>(false)

  /** Open the GitLab file page for this diagram on the default branch. */
  const handleOpenInGitLab = (): void => {
    const url: string = buildGitLabFileUrl({ repo, path: diagram.path })
    // noopener/noreferrer: the opened tab must not be able to reach this one.
    window.open(url, "_blank", "noopener,noreferrer")
  }

  /**
   * Open the editor with the history panel open. The editor reads `?history=1`
   * on load to show the history panel (task 6.5).
   */
  const handleHistory = (): void => {
    navigate(`/diagrams/${diagram.id}?history=1`)
  }

  /**
   * Delete the diagram after confirmation, then refresh the list. A 423
   * `LOCKED_BY_OTHER` means someone else holds a live Edit_Lock; that shows a
   * toast naming the holder (from the diagram's lock summary when known). Other
   * failures show their message rather than failing silently. The dialog is
   * kept open on failure so the user can retry.
   */
  const handleConfirmDelete = async (): Promise<void> => {
    setDeleting(true)
    try {
      const token: string = await getAccessToken()
      await deleteDiagram(token, diagram.id, diagram.version)
      toast.success(`Deleted "${diagram.title}"`)
      setDeleteOpen(false)
      onDeleted()
    } catch (caught: unknown) {
      if (caught instanceof DiagramsApiError && caught.code === "LOCKED_BY_OTHER") {
        // Name the editor when the list told us who holds the lock; fall back to
        // the backend's message otherwise (the lock may have been taken since
        // the list was fetched).
        const editorName: string = diagram.lock?.holderName ?? "Someone"
        toast.error(`${editorName} is editing this diagram`)
      } else {
        const message: string =
          caught instanceof Error ? caught.message : "Failed to delete diagram"
        toast.error(message)
      }
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-7 bg-background/80 text-muted-foreground hover:text-foreground"
            aria-label={`Actions for ${diagram.title}`}
          >
            <MoreHorizontal className="size-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-auto min-w-40">
          <DropdownMenuItem onSelect={() => onOpen(diagram)}>
            <SquarePen data-icon="inline-start" className="size-4" aria-hidden="true" />
            Open
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            {/* The shared CopyEmbedButton owns the clipboard-and-toast flow and
                resolves the app URL from the current origin; rendered as a
                menu row here via `asChild` so the embed format stays in one
                place. Ghost + full-width styling makes it read like the other
                items. */}
            <CopyEmbedButton
              diagram={diagram}
              repo={repo}
              variant="ghost"
              size="sm"
            />
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleOpenInGitLab}>
            <ExternalLink data-icon="inline-start" className="size-4" aria-hidden="true" />
            Open in GitLab
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleHistory}>
            <History data-icon="inline-start" className="size-4" aria-hidden="true" />
            History
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            onSelect={(event: Event) => {
              // Let the menu close, then open the confirmation dialog.
              event.preventDefault()
              setDeleteOpen(true)
            }}
          >
            <Trash2 data-icon="inline-start" className="size-4" aria-hidden="true" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this diagram?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete <strong>{diagram.title}</strong>. Any README or wiki page embedding
              this diagram will show a broken image. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Keep diagram</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={deleting}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                // Keep the dialog open until the request settles, so a failure
                // leaves it in place for a retry.
                event.preventDefault()
                void handleConfirmDelete()
              }}
            >
              {deleting ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  Deleting...
                </>
              ) : (
                "Delete it"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
